import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { ArrowRight, MapPin, Phone, Mail, ShieldCheck } from "lucide-react";
import Newsletter from "@/components/Newsletter";
import { SUPPORTED_LANGS, DEFAULT_LANG } from "@/i18n";

export default function Home() {
  const { t } = useTranslation();
  const { lang: paramLang } = useParams();
  const lang = SUPPORTED_LANGS.includes(paramLang) ? paramLang : DEFAULT_LANG;

  const stats = t("trust.stats", { returnObjects: true }) || [];
  const services = t("services.items", { returnObjects: true }) || [];
  const solutions = t("solutions.items", { returnObjects: true }) || [];
  const aboutBullets = t("about.bullets", { returnObjects: true }) || [];

  return (
    <div data-testid="home-page">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ backgroundColor: "var(--anv-ink)", color: "#fff" }}
        data-testid="hero-section"
      >
        <div className="anv-container grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 py-20 md:py-28">
          <div className="lg:col-span-7">
            <div className="anv-eyebrow" style={{ color: "var(--anv-terra)" }}>
              {t("hero.eyebrow")}
            </div>
            <h1 className="font-display mt-5 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] font-semibold text-white">
              {t("hero.title")}
            </h1>
            <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed max-w-2xl">
              {t("hero.subtitle")}
            </p>
            <div className="mt-10 flex flex-wrap gap-3">
              <a href="#newsletter" className="anv-btn-primary" data-testid="hero-primary-cta">
                {t("hero.primaryCta")} <ArrowRight size={16} />
              </a>
              <a href="#services" className="anv-btn-ghost-dark" data-testid="hero-secondary-cta">
                {t("hero.secondaryCta")}
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="border border-white/10 p-2" style={{ backgroundColor: "rgba(255,255,255,0.03)" }}>
              <img
                src="/images/hero-milan.jpg"
                alt={t("hero.imageAlt")}
                className="w-full h-[380px] lg:h-[460px] object-cover"
                loading="eager"
                data-testid="hero-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust / Stats */}
      <section className="anv-section" data-testid="trust-section">
        <div className="anv-container">
          <div className="anv-eyebrow">{t("trust.eyebrow")}</div>
          <h2 className="font-display mt-4 text-3xl md:text-4xl font-semibold max-w-3xl text-[color:var(--anv-text)]">
            {t("trust.title")}
          </h2>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 border-t border-l anv-hairline">
            {stats.map((s, i) => (
              <div
                key={i}
                className="p-6 md:p-8 border-r border-b anv-hairline bg-white"
                data-testid={`stat-card-${i}`}
              >
                <div className="font-display text-3xl md:text-4xl font-semibold text-[color:var(--anv-ink)]">
                  {s.value}
                </div>
                <div className="mt-2 text-sm text-[color:var(--anv-muted)]">{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section id="services" className="anv-section" style={{ backgroundColor: "#EFEBE3" }} data-testid="services-section">
        <div className="anv-container">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            <div className="lg:col-span-5">
              <div className="anv-eyebrow">{t("services.eyebrow")}</div>
              <h2 className="font-display mt-4 text-3xl md:text-4xl font-semibold text-[color:var(--anv-text)]">
                {t("services.title")}
              </h2>
              <p className="mt-5 text-[color:var(--anv-muted)] leading-relaxed">
                {t("services.subtitle")}
              </p>
            </div>
            <div className="lg:col-span-7 grid grid-cols-1 md:grid-cols-2 gap-4">
              {services.map((s, i) => (
                <div
                  key={s.id || i}
                  className="anv-card"
                  data-testid={`service-card-${s.id || i}`}
                >
                  <div className="text-xs font-mono text-[color:var(--anv-terra)]">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-3 font-display text-xl font-semibold text-[color:var(--anv-ink)]">
                    {s.title}
                  </div>
                  <div className="mt-3 text-sm text-[color:var(--anv-muted)] leading-relaxed">
                    {s.description}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section id="about" className="anv-section" data-testid="about-section">
        <div className="anv-container grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6 order-2 lg:order-1">
            <div className="anv-eyebrow">{t("about.eyebrow")}</div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-semibold text-[color:var(--anv-text)]">
              {t("about.title")}
            </h2>
            <p className="mt-5 text-[color:var(--anv-muted)] leading-relaxed">
              {t("about.paragraph1")}
            </p>
            <p className="mt-4 text-[color:var(--anv-muted)] leading-relaxed">
              {t("about.paragraph2")}
            </p>
            <ul className="mt-6 space-y-3">
              {aboutBullets.map((b, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-[color:var(--anv-text)]" data-testid={`about-bullet-${i}`}>
                  <ShieldCheck size={18} className="mt-0.5 flex-shrink-0" style={{ color: "var(--anv-terra)" }} />
                  <span>{b}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-6 order-1 lg:order-2">
            <div className="border border-[color:var(--anv-border)]">
              <img
                src="/images/meeting-office.jpg"
                alt={t("about.imageAlt")}
                className="w-full h-[400px] lg:h-[520px] object-cover"
                loading="lazy"
                data-testid="about-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Solutions */}
      <section id="solutions" className="anv-section" style={{ backgroundColor: "var(--anv-navy)", color: "#fff" }} data-testid="solutions-section">
        <div className="anv-container">
          <div className="anv-eyebrow" style={{ color: "var(--anv-terra)" }}>
            {t("solutions.eyebrow")}
          </div>
          <h2 className="font-display mt-4 text-3xl md:text-4xl font-semibold text-white max-w-3xl">
            {t("solutions.title")}
          </h2>
          <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
            {solutions.map((s, i) => (
              <div
                key={i}
                className="border border-white/15 p-6 md:p-8"
                style={{ backgroundColor: "rgba(255,255,255,0.03)" }}
                data-testid={`solution-card-${i}`}
              >
                <div className="text-xs font-mono text-[color:var(--anv-terra)]">
                  {String(i + 1).padStart(2, "0")} / 03
                </div>
                <div className="mt-3 font-display text-xl font-semibold text-white">
                  {s.sector}
                </div>
                <div className="mt-3 text-sm text-white/75 leading-relaxed">
                  {s.description}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section id="newsletter" className="anv-section" style={{ backgroundColor: "var(--anv-ink)", color: "#fff" }} data-testid="newsletter-section">
        <div className="anv-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="anv-eyebrow" style={{ color: "var(--anv-terra)" }}>
              {t("newsletter.eyebrow")}
            </div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-semibold text-white">
              {t("newsletter.title")}
            </h2>
            <p className="mt-5 text-white/75 leading-relaxed">
              {t("newsletter.subtitle")}
            </p>
          </div>
          <div className="lg:col-span-7">
            <Newsletter />
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="anv-section" data-testid="contact-section">
        <div className="anv-container grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="anv-eyebrow">{t("contact.eyebrow")}</div>
            <h2 className="font-display mt-4 text-3xl md:text-4xl font-semibold text-[color:var(--anv-text)]">
              {t("contact.title")}
            </h2>
            <p className="mt-5 text-[color:var(--anv-muted)] leading-relaxed max-w-lg">
              {t("contact.subtitle")}
            </p>
            <a href="#newsletter" className="anv-btn-outline mt-8 inline-flex" data-testid="contact-cta">
              {t("header.cta")} <ArrowRight size={16} />
            </a>
          </div>
          <div className="lg:col-span-6">
            <div className="border border-[color:var(--anv-border)] bg-white p-6 md:p-8">
              <div className="anv-eyebrow">{t("contact.office")}</div>
              <div className="mt-4 space-y-4 text-sm text-[color:var(--anv-text)]">
                <div className="flex items-start gap-3">
                  <MapPin size={18} className="mt-0.5" style={{ color: "var(--anv-terra)" }} />
                  <span>{t("contact.officeAddress")}</span>
                </div>
                <div className="flex items-start gap-3">
                  <Phone size={18} className="mt-0.5" style={{ color: "var(--anv-terra)" }} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.12em] text-[color:var(--anv-muted)]">
                      {t("contact.phoneLabel")}
                    </div>
                    <a href={`tel:${t("contact.phone").replace(/\s/g, "")}`} className="text-[color:var(--anv-text)] no-underline" data-testid="contact-phone">
                      {t("contact.phone")}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail size={18} className="mt-0.5" style={{ color: "var(--anv-terra)" }} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.12em] text-[color:var(--anv-muted)]">
                      {t("contact.emailLabel")}
                    </div>
                    <a href={`mailto:${t("contact.email")}`} className="text-[color:var(--anv-text)] no-underline" data-testid="contact-email">
                      {t("contact.email")}
                    </a>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <Mail size={18} className="mt-0.5" style={{ color: "var(--anv-terra)" }} />
                  <div>
                    <div className="text-xs uppercase tracking-[0.12em] text-[color:var(--anv-muted)]">
                      {t("contact.pecLabel")}
                    </div>
                    <a href={`mailto:${t("contact.pec")}`} className="text-[color:var(--anv-text)] no-underline" data-testid="contact-pec">
                      {t("contact.pec")}
                    </a>
                  </div>
                </div>
              </div>
              <div className="mt-6 -mx-6 md:-mx-8 -mb-6 md:-mb-8 border-t border-[color:var(--anv-border)]">
                <img
                  src="/images/milan-skyline.jpg"
                  alt="Milano"
                  className="w-full h-[180px] object-cover"
                  loading="lazy"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
